import { Box, Button, Flex, Image, Paragraph, Text, useColorMode } from "theme-ui";
import startIcon from "../assets/images/Icon/startIcon.svg";
import stopIcon from "../assets/images/Icon/stopIcon.svg";
import trackingIcon from "../assets/images/Icon/trackingIcon.svg";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState, useLayoutEffect, useCallback } from "react";
import { changeElapsedTime, getTimeTracking } from "../../store/actions/project.action";
import { pauseFrontAppTimer, getRunningFrontAppTask, getRunningFrontAppTaskSuccess } from "../../store/actions/frontApp.Action";
import { formatDisplayTime } from "../pages/FrontApp/TimeTrackerCell";
import { getTimeDifference, millisecondsToTime } from "../../utils/GenerateQueryString";
import { RootState } from "../../../@types";
import toast from "react-hot-toast";
import theme from "../../theme";
import GeneralTimerStop from "../pages/TaskView/GeneralTimerModal/GeneralTimerStop";

// ----- EXTRACTED DRAGGABLE COMPONENT -----
const DraggableTimeBox = ({
  displayTime,
  displayTitle,
  bgColor,
  onClick,
  onStop,
  storageKey,
  defaultPos,
  showChevron
}: any) => {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ x: number; y: number }>(() => {
    try {
      const savedRaw = localStorage.getItem(storageKey);
      if (savedRaw) {
        const saved = JSON.parse(savedRaw);
        if (saved && typeof saved.x === 'number' && typeof saved.y === 'number') {
          if (saved.x > 1 || saved.y > 1) {
            return { x: saved.x / window.innerWidth, y: saved.y / window.innerHeight };
          }
          return saved;
        }
      }
      return defaultPos;
    } catch {
      return defaultPos;
    }
  });

  const isDraggingRef = useRef(false);
  const dragStartPosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const pointerStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const wasDraggedRef = useRef(false);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(pos));
    } catch (e) { }
  }, [pos, storageKey]);

  const clampPx = (p: { x: number; y: number }) => {
    const MARGIN = 8;
    const rect = boxRef.current?.getBoundingClientRect();
    const boxW = rect?.width ?? 80;
    const boxH = rect?.height ?? 40;
    const maxX = Math.max(MARGIN, window.innerWidth - boxW - MARGIN);
    const maxY = Math.max(MARGIN, window.innerHeight - boxH - MARGIN);
    return {
      x: Math.min(Math.max(MARGIN, p.x), maxX),
      y: Math.min(Math.max(MARGIN, p.y), maxY),
    };
  };

  useLayoutEffect(() => {
    const handleResize = () => {
      const px = { x: pos.x * window.innerWidth, y: pos.y * window.innerHeight };
      const clamped = clampPx(px);
      setPos({ x: clamped.x / window.innerWidth, y: clamped.y / window.innerHeight });
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return (
    <Flex
      ref={(el) => (boxRef.current = el as any)}
      onMouseDown={(e) => {
        isDraggingRef.current = true;
        wasDraggedRef.current = false;
        dragStartPosRef.current = { x: pos.x * window.innerWidth, y: pos.y * window.innerHeight };
        pointerStartRef.current = { x: e.clientX, y: e.clientY };
        const onMove = (moveEv: MouseEvent) => {
          if (!isDraggingRef.current) return;
          const dx = moveEv.clientX - pointerStartRef.current.x;
          const dy = moveEv.clientY - pointerStartRef.current.y;
          if (Math.abs(dx) > 3 || Math.abs(dy) > 3) wasDraggedRef.current = true;
          const rawPx = { x: dragStartPosRef.current.x + dx, y: dragStartPosRef.current.y + dy };
          const clamped = clampPx(rawPx);
          setPos({ x: clamped.x / window.innerWidth, y: clamped.y / window.innerHeight });
        };
        const onUp = () => {
          isDraggingRef.current = false;
          window.removeEventListener('mousemove', onMove);
          window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
      }}
      onTouchStart={(e: any) => {
        const touch = e.touches[0];
        isDraggingRef.current = true;
        wasDraggedRef.current = false;
        dragStartPosRef.current = { x: pos.x * window.innerWidth, y: pos.y * window.innerHeight };
        pointerStartRef.current = { x: touch.clientX, y: touch.clientY };
        const onMoveTouch = (moveEv: TouchEvent) => {
          if (!isDraggingRef.current) return;
          const t = moveEv.touches[0];
          const dx = t.clientX - pointerStartRef.current.x;
          const dy = t.clientY - pointerStartRef.current.y;
          if (Math.abs(dx) > 3 || Math.abs(dy) > 3) wasDraggedRef.current = true;
          const rawPx = { x: dragStartPosRef.current.x + dx, y: dragStartPosRef.current.y + dy };
          const clamped = clampPx(rawPx);
          setPos({ x: clamped.x / window.innerWidth, y: clamped.y / window.innerHeight });
        };
        const onEndTouch = () => {
          isDraggingRef.current = false;
          window.removeEventListener('touchmove', onMoveTouch);
          window.removeEventListener('touchend', onEndTouch);
        };
        window.addEventListener('touchmove', onMoveTouch, { passive: false });
        window.addEventListener('touchend', onEndTouch);
      }}
      sx={{
        position: "fixed",
        left: `${pos.x * 100}%`,
        bottom: `${(1 - pos.y) * 100}%`,
        borderRadius: "12px",
        padding: "1rem",
        background: bgColor,
        zIndex: "999900",
        color: "#fff",
        gap: "10px",
        cursor: isDraggingRef.current ? 'grabbing' : 'grab',
        userSelect: 'none',
        transform: 'none',
      }}
      onClick={() => {
        if (wasDraggedRef.current) {
          wasDraggedRef.current = false;
          return;
        }
        if (onClick) onClick();
      }}
    >
      <Text>
        <Image src={startIcon} />
      </Text>
      <Text onClick={(e) => {
        if (onStop) {
          e.stopPropagation();
          onStop();
        }
      }}>
        <Image src={stopIcon} />
      </Text>
      <Flex sx={{ alignItems: 'center', gap: "10px" }}>
        <Text>
          <Image src={trackingIcon} />
        </Text>
        <Text>{displayTime}{showChevron && '>'}</Text>
        <Text
          sx={{
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden",
            maxWidth: "150px",
            display: "inline-block",
            cursor: "pointer",
          }}
          title={displayTitle}
        >
          {displayTitle?.length > 15 ? `${displayTitle.slice(0, 15)}...` : displayTitle}
        </Text>
      </Flex>
    </Flex>
  );
};

export default function FloatingTimeBox() {
  const { timeTracking, Latesttime } = useSelector((state: any) => state.project);
  const [colorMode] = useColorMode();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const { auther } = useSelector((state: RootState) => state.auth);
  const activeFrontAppTimer = useSelector((state: RootState) => state.frontApp?.runningFrontAppTask);

  useEffect(() => {
    if (auther?._id) {
      dispatch(getRunningFrontAppTask());
    }
  }, [auther?._id, dispatch]);

  const [now, setNow] = useState(Date.now());
  const intervalRef = useRef<any>(null);
  const reminderRef = useRef<any>(null);

  const [isTracking, setIsTracking] = useState<boolean>(false);
  const [reminderShown, setReminderShown] = useState<boolean>(false);

  const isTaskTracking = !!(timeTracking?.startTime && auther?._id === timeTracking?.userId);
  const isFrontAppTracking = !!activeFrontAppTimer;

  useEffect(() => {
    if (isTaskTracking || isFrontAppTracking) {
      setIsTracking(true)
    } else {
      setIsTracking(false)
      clearInterval(intervalRef.current)
      clearInterval(reminderRef.current);
    }
  }, [timeTracking, activeFrontAppTimer, isTaskTracking, isFrontAppTracking]);

  const isTaskTrackingRef = useRef(isTaskTracking);
  const timeTrackingRef = useRef(timeTracking);
  useEffect(() => {
    isTaskTrackingRef.current = isTaskTracking;
    timeTrackingRef.current = timeTracking;
  }, [isTaskTracking, timeTracking]);

  const updateTimer = useCallback(() => {
    setNow(Date.now());
    if (isTaskTrackingRef.current) {
      const diffMs = getTimeDifference(timeTrackingRef.current?.startTime);
      const newFormatTime = millisecondsToTime(diffMs);
      dispatch(changeElapsedTime(newFormatTime));
    }
  }, [dispatch]);

  useEffect(() => {
    if (isTracking) {
      updateTimer(); // Call once immediately
      intervalRef.current = setInterval(updateTimer, 1000);
      reminderRef.current = setInterval(() => {
        toast(
          (t) => (
            <div >
              <Paragraph variant="Poppins16Bold" >Time tracking is currently In progress!⏳</Paragraph>
              <Box className="mt-2">
                <Button
                  sx={{ paddingTop: '10px', paddingBottom: '10px' }}
                  onClick={() => {
                    toast.dismiss(t.id);
                    setReminderShown(true);
                  }}
                >
                  OK
                </Button></Box>
            </div>
          ),
          { duration: Infinity }
        );
      }, 3600000); // 3600000 ms = 1 hour

    } else {
      setIsTracking(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      clearTimeout(reminderRef.current);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (reminderRef.current) {
        clearTimeout(reminderRef.current);
      }
    };
  }, [isTracking, updateTimer]);

  useEffect(() => {
    dispatch(getTimeTracking())
  }, [dispatch, Latesttime])

  const goToTrackingTask = (() => {
    let goTo = `/edit-task/${timeTracking?.taskId?._id}`
    if (pathname !== goTo) {
      navigate(`edit-task/${timeTracking?.taskId?._id}`)
    }
  })

  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };

  // --- FrontApp Properties ---
  let frontAppDisplayTime = '0m 00s';
  let frontAppBgColor = theme.colors?.secondary as string;
  let frontAppTitle = 'FrontApp Timer';

  if (isFrontAppTracking && activeFrontAppTimer) {
    if (activeFrontAppTimer.timer) {
      const timer = activeFrontAppTimer.timer;
      let elapsedMs = timer.accumulatedTime || 0;
      if (timer.lastStartedAt) {
        elapsedMs += Math.max(0, now - new Date(timer.lastStartedAt).getTime());
      }
      frontAppDisplayTime = formatDisplayTime(elapsedMs, true);
    }

    frontAppTitle = activeFrontAppTimer.tabname ? `${activeFrontAppTimer.tabname} > Frontapp` : 'FrontApp Timer';

    let isOverdue = false;
    const itemDateStr = activeFrontAppTimer.createdAt || activeFrontAppTimer.created_at;
    if (itemDateStr && activeFrontAppTimer.PM !== 'Completed') {
      let parsedDateMs = new Date(itemDateStr).getTime();
      if (isNaN(parsedDateMs) && typeof itemDateStr === 'string') {
        const parts = itemDateStr.split(/[-/]/);
        if (parts.length === 3) {
          if (parts[0].length === 4) {
            parsedDateMs = new Date(`${parts[0]}-${parts[1]}-${parts[2]}`).getTime();
          } else if (parts[2].length === 4) {
            parsedDateMs = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`).getTime();
          }
        }
      }
      if (!isNaN(parsedDateMs)) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const taskDate = new Date(parsedDateMs);
        taskDate.setHours(0, 0, 0, 0);
        const diffTime = today.getTime() - taskDate.getTime();
        const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
        isOverdue = diffDays >= 4;
      }
    }

    if (isOverdue) {
      frontAppBgColor = colorMode === 'light'
        ? 'linear-gradient(90deg, rgba(214, 88, 88, 0.4) 0%, rgba(214, 88, 88, 0.25) 50%, rgba(214, 88, 88, 0.08) 100%)'
        : 'linear-gradient(90deg, rgba(217, 105, 105, 0.233) 0%, rgba(153, 80, 80, 0.5) 40%, rgba(41, 47, 55, 0.326) 75%, rgba(42, 34, 34, 0.41) 100%)';
    } else {
      frontAppBgColor = activeFrontAppTimer?.DT === 'Completed' || activeFrontAppTimer?.PM === 'Completed' ? theme.colors?.green as string : '#0e5f80';
    }
  }

  // --- Task Tracking Properties ---
  let taskDisplayTime = '0m 00s';
  let taskBgColor = theme.colors?.secondary as string;
  let taskTitle = timeTracking?.title || '';

  if (isTaskTracking && timeTracking) {
    if (timeTracking.startTime) {
      const diffMs = now - timeTracking.startTime;
      taskDisplayTime = formatDisplayTime(Math.max(0, diffMs), true);
    }
    taskTitle = timeTracking.category === 'Task timer'
      ? `${timeTracking.projectId?.name} > ${timeTracking.stageId?.temporaryName || timeTracking.stageId?.name} > ${timeTracking.taskId?.name}`
      : timeTracking.title;
  }

  return (
    <>
      {isFrontAppTracking && activeFrontAppTimer && (
        <DraggableTimeBox
          storageKey="floatingTimeBoxPos_frontapp"
          defaultPos={{ x: (window.innerWidth / 2 - 100) / window.innerWidth, y: (window.innerHeight - 120) / window.innerHeight }}
          displayTime={frontAppDisplayTime}
          displayTitle={frontAppTitle}
          bgColor={frontAppBgColor}
          showChevron={false}
          onClick={() => {
            navigate(`/front-app/pending-items?manager=${auther?._id}&isRunning=true`);
          }}
          onStop={() => {
            const searchParams = new URLSearchParams(location.search);
            if (searchParams.has('isRunning')) {
              searchParams.delete('isRunning');
              navigate(`${pathname}?${searchParams.toString()}`);
            }
            dispatch(pauseFrontAppTimer(activeFrontAppTimer._id));
            dispatch(getRunningFrontAppTaskSuccess(null));
          }}
        />
      )}

      {isTaskTracking && timeTracking?.startTime && (
        <DraggableTimeBox
          storageKey="floatingTimeBoxPos_task"
          defaultPos={{ x: (window.innerWidth / 2 - 100) / window.innerWidth, y: (window.innerHeight - 190) / window.innerHeight }}
          displayTime={taskDisplayTime}
          displayTitle={taskTitle}
          bgColor={taskBgColor}
          showChevron={timeTracking?.category !== 'task timer'}
          onClick={() => {
            if (timeTracking?.category === 'Task timer') {
              goToTrackingTask();
            } else {
              toggleModal();
            }
          }}
          onStop={undefined} // Standard task handles stop implicitly via onClick
        />
      )}

      {/* The modal is originally rendered here */}
      <GeneralTimerStop isOpen={modalOpen} toggle={toggleModal} projectId={timeTracking?.projectId?._id} stageId={timeTracking?.stageId?._id} />
    </>
  )
}
